/**
 * Modal - the app's only dialog primitive (docs/FRONTEND-PLAN.md section 2.1).
 *
 * Stacking order (docs/DESIGN-SYSTEM.md): sticky header z-50, skip link
 * z-100, modal z-200, toasts z-300, so a toast stays readable above a dialog.
 *
 * Accessibility contract:
 * - Portalled to <body> so it is never clipped by a card/overflow ancestor.
 * - `role="dialog"` + `aria-modal="true"`, labelled by its own title.
 * - Focus moves in on open, Tab/Shift+Tab cycle inside, and the previously
 *   focused element is restored on close.
 * - Esc and a backdrop click both close; body scroll is locked while open.
 */
import { useCallback, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

export default function Modal({ open, onClose, title, children, footer }) {
  const dialogRef = useRef(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return undefined;

    const restoreTo = document.activeElement;
    const { body } = document;
    const previousOverflow = body.style.overflow;
    body.style.overflow = 'hidden';

    const dialog = dialogRef.current;
    const first = dialog && dialog.querySelector(FOCUSABLE);
    if (first) first.focus();
    else if (dialog) dialog.focus();

    return () => {
      body.style.overflow = previousOverflow;
      if (restoreTo && typeof restoreTo.focus === 'function') restoreTo.focus();
    };
  }, [open]);

  const handleKeyDown = useCallback(
    event => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(dialog.querySelectorAll(FOCUSABLE));
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose]
  );

  // mousedown (not click) so a text selection that ends on the backdrop does
  // not dismiss the dialog.
  const handleBackdrop = useCallback(
    event => {
      if (event.target === event.currentTarget) onClose();
    },
    [onClose]
  );

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-200 flex items-center justify-center bg-dcon-scrim p-4"
      onKeyDown={handleKeyDown}
      onMouseDown={handleBackdrop}
    >
      <div
        className="flex max-h-full w-full max-w-lg flex-col overflow-hidden rounded-z-card bg-z-raised text-dcon-ink-body shadow-dcon-lg focus:outline-none"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={dialogRef}
        tabIndex={-1}
      >
        <header className="flex items-start justify-between gap-3 px-6 pt-6 pb-3">
          <h2 className="m-0 text-title-s" id={titleId}>
            {title}
          </h2>
          <button
            type="button"
            className="cursor-pointer rounded-dcon-sm border-0 bg-transparent px-1 text-title-m leading-none text-dcon-ink-muted hover:text-dcon-ink"
            aria-label="Close dialog"
            onClick={onClose}
          >
            &times;
          </button>
        </header>
        <div className="overflow-y-auto px-6 pb-6 *:first:mt-0 *:last:mb-0">{children}</div>
        {footer && (
          <footer className="flex flex-wrap justify-end gap-2 border-t border-z-line px-6 pt-3 pb-6">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body
  );
}
